import {
    Component,
    ElementRef,
    EventEmitter,
    HostListener,
    Input,
    OnChanges,
    OnDestroy,
    OnInit,
    Output,
    SimpleChanges
} from '@angular/core';
import { FormBuilder, FormControl, FormGroup } from '@angular/forms';
import { UrgencyType } from '@app/api-module/control-centre-api-client';
import { CommonUtilities } from '@app/shared/common-utilities';
import { BehaviorSubject, Observable, Subscription, combineLatest, map } from 'rxjs';

@Component({
    selector: 'app-imaging-requests-filters',
    templateUrl: './imaging-requests-filters.component.html',
    styleUrl: './imaging-requests-filters.component.scss'
})
export class ImagingRequestsFiltersComponent implements OnInit, OnChanges, OnDestroy {
    @Input()
    referringDoctors: string[];

    dirty$: Observable<boolean>;
    hasFilters$: Observable<boolean>;

    visible$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);

    opening: boolean;

    doctorsFormLoaded$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);

    private static _appliedUrgencyFilters: BehaviorSubject<UrgencyType[]> = new BehaviorSubject<UrgencyType[]>([]);
    private readonly _filteredUrgencyTypes: BehaviorSubject<UrgencyType[]> = new BehaviorSubject<UrgencyType[]>([]);
    urgenciesForm: FormGroup;

    private static _appliedDoctorFilters: BehaviorSubject<string[]> = new BehaviorSubject<string[]>([]);
    private readonly _filteredDoctors: BehaviorSubject<string[]> = new BehaviorSubject<string[]>([]);
    doctorsForm: FormGroup;

    @Output()
    urgencyTypeFiltersUpdated: EventEmitter<UrgencyType[]> = new EventEmitter<UrgencyType[]>();

    @Output()
    doctorFiltersUpdated: EventEmitter<string[]> = new EventEmitter<string[]>();

    private readonly _subscriptions: Subscription = new Subscription();

    @HostListener('document:click', ['$event'])
    clickout(event: MouseEvent) {
        // Close filters popout if people click outside it, and wasn't just opened.
        if (this.visible$.value && !this.opening && !this.eRef.nativeElement.contains(event.target)) {
            this.close();
        }
    }

    constructor(formBuilder: FormBuilder, private readonly eRef: ElementRef) {
        this.urgenciesForm = formBuilder.group({
            Trauma: new FormControl(false),
            Stroke: new FormControl(false)
        });
        this.doctorsForm = formBuilder.group({});
    }

    ngOnInit(): void {
        this._subscriptions.add(
            ImagingRequestsFiltersComponent._appliedUrgencyFilters.subscribe(urgencyTypes => {
                this._filteredUrgencyTypes.next(urgencyTypes);
                urgencyTypes =
                    urgencyTypes.includes(UrgencyType.Stroke) || urgencyTypes.includes(UrgencyType.Trauma)
                        ? [...urgencyTypes, UrgencyType.TraumaAndStroke]
                        : [...urgencyTypes];
                this.urgencyTypeFiltersUpdated.emit(urgencyTypes);
            })
        );
        this._subscriptions.add(
            ImagingRequestsFiltersComponent._appliedDoctorFilters.subscribe(doctors => {
                this._filteredDoctors.next(doctors);
                this.doctorFiltersUpdated.emit(doctors);
            })
        );
        this.hasFilters$ = combineLatest([this._filteredUrgencyTypes, this._filteredDoctors]).pipe(
            map(([types, doctors]) => types.length > 0 || doctors.length > 0)
        );
        this.dirty$ = combineLatest([
            ImagingRequestsFiltersComponent._appliedUrgencyFilters,
            this._filteredUrgencyTypes,
            ImagingRequestsFiltersComponent._appliedDoctorFilters,
            this._filteredDoctors
        ]).pipe(
            map(
                ([appliedUrgencyFilters, filteredUrgencyTypes, appliedDoctorFilters, filteredDoctors]) =>
                    !CommonUtilities.arraysEqual(appliedUrgencyFilters, filteredUrgencyTypes) ||
                    !CommonUtilities.arraysEqual(appliedDoctorFilters, filteredDoctors)
            )
        );

        this._subscriptions.add(
            this._filteredUrgencyTypes.subscribe(urgencyTypes => {
                for (const urgencyKey in this.urgenciesForm.controls) {
                    const urgency = urgencyKey as UrgencyType;
                    this.urgenciesForm.patchValue({ [urgencyKey]: urgencyTypes.includes(urgency) });
                }
            })
        );

        this._subscriptions.add(
            this._filteredDoctors.subscribe(doctors => {
                for (const doctorKey in this.doctorsForm.controls) {
                    this.doctorsForm.patchValue({ [doctorKey]: doctors.includes(doctorKey) });
                }
            })
        );

        this._subscriptions.add(
            this.visible$.subscribe(visible => {
                if (visible) {
                    this.opening = true;
                    setTimeout(() => {
                        this.opening = false;
                    }, 250);
                }
            })
        );

        this.referringDoctors.forEach(doctor => this.doctorsForm.addControl(doctor, new FormControl(false)));
        this.doctorsFormLoaded$.next(true);
    }

    ngOnChanges(changes: SimpleChanges): void {
        const currentDoctors = new Set<string>(changes.referringDoctors.currentValue);
        const previousDoctors = new Set<string>(changes.referringDoctors.previousValue);
        const newDoctors = [...currentDoctors].filter(doctor => !previousDoctors.has(doctor));
        const removedDoctors = [...previousDoctors].filter(doctor => !currentDoctors.has(doctor));

        this.doctorsFormLoaded$.next(false);
        newDoctors.forEach(doctor => this.doctorsForm.addControl(doctor, new FormControl(false)));
        removedDoctors.forEach(doctor => this.doctorsForm.removeControl(doctor));
        this.doctorsFormLoaded$.next(true);
    }

    changeUrgency(event, urgency): void {
        const urgencyType = UrgencyType[urgency];
        if (event.checked) {
            this.addUrgency(urgencyType);
        } else {
            this.removeUrgency(urgencyType);
        }
    }

    addUrgency(urgencyType: UrgencyType): void {
        this._filteredUrgencyTypes.next([...this._filteredUrgencyTypes.value, urgencyType]);
    }

    removeUrgency(urgencyType: UrgencyType): void {
        this._filteredUrgencyTypes.next([...this._filteredUrgencyTypes.value.filter(filteredType => filteredType !== urgencyType)]);
    }

    changeDoctor(event, doctor): void {
        if (event.checked) {
            this._filteredDoctors.next([...this._filteredDoctors.value, doctor]);
        } else {
            this._filteredDoctors.next([...this._filteredDoctors.value.filter(filteredDoctor => filteredDoctor !== doctor)]);
        }
    }

    clearFilters(): void {
        ImagingRequestsFiltersComponent._appliedUrgencyFilters.next([]);
        ImagingRequestsFiltersComponent._appliedDoctorFilters.next([]);
    }

    submitFilters(): void {
        ImagingRequestsFiltersComponent._appliedUrgencyFilters.next(this._filteredUrgencyTypes.value);
        ImagingRequestsFiltersComponent._appliedDoctorFilters.next(this._filteredDoctors.value);
        this.close();
    }

    toggleVisibility(): void {
        if (!this.visible$.value) {
            this.visible$.next(true);
        } else {
            this.close();
        }
    }

    resetCheckboxes(): void {
        ImagingRequestsFiltersComponent._appliedDoctorFilters.next(ImagingRequestsFiltersComponent._appliedDoctorFilters.value);
        ImagingRequestsFiltersComponent._appliedUrgencyFilters.next(ImagingRequestsFiltersComponent._appliedUrgencyFilters.value);
    }

    close(): void {
        this.visible$.next(false);
        this.resetCheckboxes();
    }

    ngOnDestroy(): void {
        this._subscriptions.unsubscribe();
    }
}
